import { useContext } from 'react'
import { useIntl } from 'react-intl'
import { EOrgType } from '@unfoldrtech/portal-mic'
import { EDatasetKey } from '@unfoldrtech/portal-mic/dist/src/models/core'

import { AppContext } from '../../../models'
import {
  TDatasetWhitelistKey,
  TGlobalMetricData,
  TGlobalMetrics,
} from '../models'
import { PORTAL_FF_REPORTING_METRICS_OMIT } from '../../../utils/constants'
import { useFeatureFlags } from '../../../hooks/useFeatureFlags'
import { isCurrency, isPercentage } from '../helpers'
import {
  metricTypeMap,
  genericMetricNames,
  spMetricNames,
  daMetricNames,
  doohMetricNames,
  daKeywordMetricNames,
  daProductMetricNames,
  spProductMetricNames,
  genericAdgroupSearchMetricNames,
  spKeywordMetricNames,
  daAudienceMetricNames,
  doohAudienceMetricNames,
  genericAdvertiserMetricNames,
} from './constants'

export const useGetDatasetKeyChartWhitelist = ({
  key,
}: {
  key: TDatasetWhitelistKey
}) => {
  const intl = useIntl()
  const featureFlags = useFeatureFlags()

  const [appContext] = useContext(AppContext)

  const orgType = appContext.userOrg?.type || EOrgType.Advertiser

  const whitelist = {} as Record<TDatasetWhitelistKey, Array<TGlobalMetricData>>

  const getGlobalMetricData = ({
    metricName,
  }: {
    metricName: TGlobalMetrics
  }): TGlobalMetricData => {
    return {
      metricName,
      translatedMetricName: intl.formatMessage({
        id: `report.table.header.${orgType?.toLowerCase()}.${metricName}`,
      }),
      metricNumberType: metricTypeMap[metricName].aggregationType,
      isCurrency: isCurrency(metricName),
      isPercentage: isPercentage(metricName),
    }
  }

  const getChartWhitelist = (metricNamesList: Array<TGlobalMetrics>) => {
    return metricNamesList.map((metricName) =>
      getGlobalMetricData({ metricName })
    )
  }

  whitelist[EDatasetKey.Advertiser] = getChartWhitelist([
    ...genericAdvertiserMetricNames,
  ])

  whitelist[EDatasetKey.CampaignGeneric] = getChartWhitelist([
    ...genericMetricNames,
  ])
  whitelist[EDatasetKey.CampaignSp] = getChartWhitelist([...spMetricNames])
  whitelist[EDatasetKey.CampaignDa] = getChartWhitelist([...daMetricNames])
  whitelist[EDatasetKey.CampaignDooh] = getChartWhitelist([...doohMetricNames])

  whitelist[EDatasetKey.AdgroupGeneric] = getChartWhitelist([
    ...genericMetricNames,
  ])
  whitelist[EDatasetKey.AdgroupGenericCategory] = getChartWhitelist([
    ...genericMetricNames,
  ])
  whitelist[EDatasetKey.AdgroupGenericKeyword] = getChartWhitelist([
    ...genericAdgroupSearchMetricNames, // exception
  ])
  whitelist[EDatasetKey.AdgroupDaPagePosition] = getChartWhitelist([
    ...daMetricNames,
  ])
  whitelist[EDatasetKey.AdgroupDoohScreen] = getChartWhitelist([
    ...doohMetricNames,
  ])

  whitelist[EDatasetKey.KeywordSp] = getChartWhitelist([
    ...spKeywordMetricNames, // exception
  ])
  whitelist[EDatasetKey.KeywordDa] = getChartWhitelist([
    ...daKeywordMetricNames, // exception
  ])
  whitelist[EDatasetKey.CategorySp] = getChartWhitelist([...spMetricNames])
  whitelist[EDatasetKey.CategoryDa] = getChartWhitelist([...daMetricNames])
  whitelist[EDatasetKey.ProductSp] = getChartWhitelist([
    ...spProductMetricNames, // exception
  ])
  whitelist[EDatasetKey.ProductDa] = getChartWhitelist([
    ...daProductMetricNames, // exception
  ])

  whitelist[EDatasetKey.AdgroupSp] = getChartWhitelist([...spMetricNames])
  whitelist[EDatasetKey.AdgroupDa] = getChartWhitelist([...daMetricNames])
  whitelist[EDatasetKey.AdgroupDooh] = getChartWhitelist([...doohMetricNames])

  whitelist[EDatasetKey.CreativeDa] = getChartWhitelist([...daMetricNames])
  whitelist[EDatasetKey.CreativeDooh] = getChartWhitelist([...doohMetricNames])

  whitelist[EDatasetKey.PositionDa] = getChartWhitelist([...daMetricNames])
  whitelist[EDatasetKey.AudienceDa] = getChartWhitelist([
    ...daAudienceMetricNames, // exception
  ])
  whitelist[EDatasetKey.AudienceDooh] = getChartWhitelist([
    ...doohAudienceMetricNames, // exception
  ])

  whitelist[EDatasetKey.ProductSpAdgroup] = getChartWhitelist([
    ...spProductMetricNames, // exception
  ])
  whitelist[EDatasetKey.ProductDaAdgroup] = getChartWhitelist([
    ...daProductMetricNames, // exception
  ])

  whitelist[EDatasetKey.CategorySpAdgroup] = getChartWhitelist([
    ...spMetricNames,
  ])
  whitelist[EDatasetKey.CategoryDaAdgroup] = getChartWhitelist([
    ...daMetricNames,
  ])

  whitelist[EDatasetKey.KeywordSpAdgroup] = getChartWhitelist([
    ...spKeywordMetricNames, // exception
  ])
  whitelist[EDatasetKey.KeywordDaAdgroup] = getChartWhitelist([
    ...daKeywordMetricNames, // exception
  ])
  whitelist[EDatasetKey.PositionDaAdgroupPagePosition] = getChartWhitelist([
    ...daMetricNames,
  ])
  whitelist[EDatasetKey.PositionDaAdgroupCategory] = getChartWhitelist([
    ...daMetricNames,
  ])
  whitelist[EDatasetKey.PositionDaAdgroupKeyword] = getChartWhitelist([
    ...daMetricNames,
  ])
  whitelist[EDatasetKey.AudienceDaAdgroup] = getChartWhitelist([
    ...daAudienceMetricNames, // exception
  ])
  whitelist[EDatasetKey.AudienceDoohAdgroup] = getChartWhitelist([
    ...doohAudienceMetricNames, // exception
  ])
  whitelist[EDatasetKey.LocationDooh] = getChartWhitelist([...doohMetricNames])

  whitelist[EDatasetKey.CreativeDaAdgroup] = getChartWhitelist([
    ...daMetricNames,
  ])
  whitelist[EDatasetKey.CreativeDoohAdgroup] = getChartWhitelist([
    ...doohMetricNames,
  ])
  whitelist[EDatasetKey.SearchTermSp] = getChartWhitelist([...spMetricNames])
  whitelist[EDatasetKey.SearchTermDa] = getChartWhitelist([...daMetricNames])

  if (
    featureFlags[PORTAL_FF_REPORTING_METRICS_OMIT] &&
    featureFlags[PORTAL_FF_REPORTING_METRICS_OMIT].length
  ) {
    whitelist[key].filter(
      (column) =>
        !featureFlags[PORTAL_FF_REPORTING_METRICS_OMIT].includes(
          column.metricName
        )
    )
  }

  return whitelist[key]
}
